<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import EmptyState from "$lib/components/EmptyState.svelte";
  import { categories, type Category } from "$lib/stores/categories.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { onMount } from "svelte";

  let kindFilter = $state<"all" | "income" | "expense">("all");
  let showForm = $state(false);
  let editing = $state<Category | null>(null);

  let form = $state<{ name: string; kind: "income" | "expense"; color: string; icon: string }>({
    name: "", kind: "expense", color: "#0f766e", icon: "💸",
  });

  onMount(() => { categories.ensureSeeded(); });

  let visible = $derived(
    kindFilter === "all" ? categories.list : categories.list.filter((c) => c.kind === kindFilter),
  );

  function openCreate() {
    editing = null;
    form = { name: "", kind: "expense", color: "#0f766e", icon: "💸" };
    showForm = true;
  }

  function openEdit(c: Category) {
    editing = c;
    form = { name: c.name, kind: c.kind, color: c.color ?? "#0f766e", icon: c.icon ?? "💸" };
    showForm = true;
  }

  async function save(e: Event) {
    e.preventDefault();
    try {
      if (editing) {
        await categories.update(editing.id, { name: form.name, color: form.color, icon: form.icon });
        toasts.success("Categoria atualizada");
      } else {
        await categories.create(form);
        toasts.success("Categoria criada");
      }
      showForm = false;
    } catch (e: any) {
      toasts.error("Erro", e.message);
    }
  }

  async function remove(c: Category) {
    if (!confirm(`Remover categoria "${c.name}"?`)) return;
    try { await categories.remove(c.id); toasts.success("Removida"); }
    catch (e: any) { toasts.error("Não foi possível remover", e.message); }
  }
</script>

<Navbar />
<main class="max-w-5xl mx-auto px-4 py-6">
  <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
    <h1 class="text-2xl font-bold">Categorias</h1>
    <div class="flex items-center gap-2">
      <select class="input w-auto" bind:value={kindFilter}>
        <option value="all">Todas</option>
        <option value="expense">Despesas</option>
        <option value="income">Receitas</option>
      </select>
      <button class="btn-primary" onclick={openCreate}>+ Nova</button>
    </div>
  </div>

  {#if visible.length === 0}
    <EmptyState icon="🏷️" title="Nenhuma categoria"
                description="Categorize suas transações para ter relatórios melhores."
                actionLabel="Criar primeira categoria" onAction={openCreate} />
  {:else}
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {#each visible as c}
        <div class="card flex items-center gap-3">
          <div class="w-10 h-10 rounded-full flex items-center justify-center text-xl"
               style="background: {c.color ?? '#64748b'}22; color: {c.color ?? '#64748b'}">
            {c.icon ?? "•"}
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium truncate">{c.name}</div>
            <div class="text-xs text-muted">{c.kind === "income" ? "Receita" : "Despesa"}</div>
          </div>
          <button class="btn-ghost text-sm" onclick={() => openEdit(c)} aria-label="Editar">✏️</button>
          <button class="btn-ghost text-sm text-red-600" onclick={() => remove(c)} aria-label="Remover">🗑️</button>
        </div>
      {/each}
    </div>
  {/if}

  {#if showForm}
    <div class="modal-backdrop" role="dialog" aria-modal="true">
      <button type="button" class="absolute inset-0" aria-label="Fechar" onclick={() => (showForm = false)}></button>
      <form class="modal-panel space-y-3" onsubmit={save}>
        <h2 class="text-lg font-semibold">{editing ? "Editar" : "Nova"} categoria</h2>
        <div>
          <label class="label" for="cn">Nome</label>
          <input id="cn" class="input" bind:value={form.name} required maxlength="120" />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="label" for="ck">Tipo</label>
            <select id="ck" class="input" bind:value={form.kind} disabled={!!editing}>
              <option value="expense">Despesa</option>
              <option value="income">Receita</option>
            </select>
          </div>
          <div>
            <label class="label" for="cc">Cor</label>
            <input id="cc" type="color" class="input h-10 p-1" bind:value={form.color} />
          </div>
        </div>
        <div>
          <label class="label" for="ci">Ícone (1 emoji)</label>
          <input id="ci" class="input" bind:value={form.icon} maxlength="4" />
        </div>
        <div class="flex justify-end gap-2 pt-2">
          <button type="button" class="btn-ghost" onclick={() => (showForm = false)}>Cancelar</button>
          <button class="btn-primary" type="submit">Salvar</button>
        </div>
      </form>
    </div>
  {/if}
</main>
